import { Alert, Box, Button } from "@mui/material";
import { useState } from "react";
import { PostForMeAccount, EffectiveValues } from "./models";
import { PostCreateForm } from "./PostCreateForm";
import FacebookPostPreview from "./Previews/FacebookPostPreview";
import FacebookReelsPreview from "./Previews/FacebookReelsPreview";
import FacebookStoriesPreview from "./Previews/FacebookStoriesPreview";
import InstagramPostPreview from "./Previews/InstagramPostPreview";
import InstagramStoriesPreview from "./Previews/InstagramStoriesPreview";
import InstagramReelsPreview from "./Previews/InstagramReelsesPreview";
import TikTokPreview from "./Previews/TikTokPreview";
import { Typography } from "@mui/material";
import { usePostsConfigAPI } from "../../fans/settings/settings.api";
import PurchasePostsDialog from "../../../components/SubscriptionDetails/components/SubscriptionInfo/PurchasePostsDialog";

type Props = {
  accounts: PostForMeAccount[];
  selectedAccountIds: string[];
  setSelectedAccountIds: (ids: string[]) => void;
  submitPost: (body: any) => void;
};

const VALID_PLACEMENTS = ["timeline", "reels", "stories"];

export default function PostPresenter({
  accounts,
  selectedAccountIds,
  setSelectedAccountIds,
  submitPost
}: Props) {
  const [fbEffective, setFbEffective] = useState<EffectiveValues | null>(null);
  const [igEffective, setIgEffective] = useState<EffectiveValues | null>(null);
  const [ttEffective, setTtEffective] = useState<EffectiveValues | null>(null);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  const hasAnyPreview = Boolean(
    (fbEffective && VALID_PLACEMENTS.includes(fbEffective.placement)) ||
    (igEffective && VALID_PLACEMENTS.includes(igEffective.placement)) ||
    ttEffective
  );

  const { data: postsConfig } = usePostsConfigAPI();
  const availablePosts = (postsConfig?.postsLimitCurrent ?? 0) + (postsConfig?.additionalPostsLimit ?? 0);
  const noPostsLeft = availablePosts <= 0;

  const handleOpenModal = () => setPurchaseModalOpen(true);
  const handleCloseModal = () => setPurchaseModalOpen(false);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerRight}>
        {noPostsLeft && (
          <Alert variant="filled" severity="warning" sx={styles.alert}>
            You have no social post credits left.
          </Alert>
        )}
        <Button variant="contained" color="secondary" onClick={handleOpenModal}>
          Buy More Posts
        </Button>
        <Typography sx={styles.availableText}>
          {availablePosts.toLocaleString()} social posts are available.
        </Typography>
      </Box>
      <Box sx={styles.root}>

        <Box sx={styles.column}>
          <PostCreateForm
            accounts={accounts}
            selectedAccountIds={selectedAccountIds}
            setSelectedAccountIds={setSelectedAccountIds}
            submitPost={submitPost}
            onPreviewChange={(p) => {
              setFbEffective(p.facebook ?? null);
              setIgEffective(p.instagram ?? null);
              setTtEffective(p.tiktok ?? null);
            }}
          />
        </Box>

        <Box sx={styles.column}>
          <Box sx={styles.previews}>
            {!hasAnyPreview && (
              <Box sx={styles.previewsLabel} role="status" aria-label="Posts previews">
                Start creating! Your preview will show up here.
              </Box>
            )}

            {fbEffective && fbEffective.placement === "timeline" && (
              <FacebookPostPreview
                caption={fbEffective.caption}
                media={fbEffective.media}
                placement={fbEffective.placement}
              />
            )}
            {fbEffective && fbEffective.placement === "reels" && (
              <FacebookReelsPreview
                media={fbEffective.media}
                caption={fbEffective.caption}
              />
            )}
            {fbEffective && fbEffective.placement === "stories" && (
              <FacebookStoriesPreview
                media={fbEffective.media}
                caption={fbEffective.caption}
              />
            )}

            {igEffective && igEffective.placement === "timeline" && (
              <InstagramPostPreview
                media={igEffective.media}
                caption={igEffective.caption}
              />
            )}
            {igEffective && igEffective.placement === "stories" && (
              <InstagramStoriesPreview
                media={igEffective.media}
                caption={igEffective.caption}
              />
            )}
            {igEffective && igEffective.placement === "reels" && (
              <InstagramReelsPreview
                media={igEffective.media}
                caption={igEffective.caption}
              />
            )}

            {ttEffective && (
              <TikTokPreview
                media={ttEffective.media}
                caption={ttEffective.caption}
              />
            )}
          </Box>
        </Box>
      </Box>
      {purchaseModalOpen && <PurchasePostsDialog onClose={handleCloseModal} />}
    </Box>
  );
}

const styles = {
  container: {
    width: 1
  },
  root: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: 3,
    width: 1
  },
  headerRight: {
    width: 1,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 1,
    mb: 1
  },
  availableText: {
    color: "text.secondary",
    fontWeight: 500
  },
  alert: {
    py: 0.5,
    px: 1
  },
  column: {
    flexGrow: 1,
    flexBasis: { md: "50%" },
    minWidth: 0
  },
  previews: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    alignItems: "center"
  },
  previewsLabel: {
    mt: { xs: 6, md: 28 },
    px: 2,
    py: 1,
    fontSize: 14,
    fontWeight: 500,
    color: "text.secondary",
    bgcolor: "background.default",
    border: "2px dashed",
    borderColor: "divider",
    borderRadius: 4,
    textAlign: "center",
    maxWidth: 420,
    width: "fit-content"
  }
};
